<script>
import { Search } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import Button from "@/shared/ui/button/Button.vue";
import dateFormat from "@/shared/lib/date-format";
import { getRoleList, removeRole } from "@/entities/role";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";

export default {
  name: "ConstructorActions",
  components: {
    TableFooterWrapper,
    Search,
    Input,
    Button,
    TableRowActions,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
  },
  data() {
    return {
      loading: false,
      list: [],
      organizations: null,
      roles: null,
      total: 0,

      filters: {
        q: "",
        page: 1,
        page_size: 20,
      },

      pageSizes: [1, 5, 10, 20],
    };
  },
  computed: {
    canCreate() {
      return this.$store.getters.has_access("ACR");
    },
    actions() {
      const list = [];
      if (this.$store.getters.has_access("ADR")) {
        list.push({
          label: "Деактивировать",
          type: "danger",
          action: "block",
          icon: "huge-close",
        });
      }
      if (this.$store.getters.has_access("AUR")) {
        list.push({
          label: "Редактировать",
          type: "default",
          action: "edit",
          icon: "huge-pen2",
        });
      }
      if (this.$store.getters.has_access("ADR")) {
        list.push({
          label: "Удалить",
          type: "danger",
          action: "remove",
          icon: "huge-danger",
        });
      }
      list.push({
        label: "Просмотр роли",
        type: "default",
        action: "view",
        icon: "huge-eye",
        isLast: true,
      });
      return list;
    },
  },
  watch: {
    filters: {
      handler() {
        this.getRoleList();
      },
      deep: true,
    },
  },
  mounted() {
    this.getRoleList();
  },
  methods: {
    async getRoleList() {
      let params = {
        page_size: this.filters.page_size,
        page: this.filters.page,
      };
      if (this.filters.q) {
        params.q = this.filters.q.trim().toLowerCase();
      }
      this.loading = true;

      try {
        let data = await getRoleList(params);

        if (data?.success) {
          if (data.payload) {
            this.total = data.payload?.total;
            this.filters.page_size = data.payload?.page_size;
            this.filters.page = data.payload?.page;
            this.list = data.payload.items || [];
          }
        }
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.filters.page = page;
    },
    goToCreate() {
      this.$router.push({
        name: "role_new",
        params: { id: "new" },
      });
    },
    dateFormat(date) {
      if (!date) {
        return this.$t("Не указано");
      }
      return dateFormat.timestamp_to_local_tz(date, "DD.MM.YYYY, HH:mm");
    },
    async handleActions(item) {
      if (item.action === "edit") {
        this.$router.push({
          name: "role_item",
          params: { id: item.uuid },
        });
      } else if (item.action === "view") {
        this.$router.push({
          name: "role_item_view",
          params: { id: item.uuid },
        });
      } else if (item.action === "remove") {
        await this.removeRole(item.uuid);
      }
    },
    async removeRole(id) {
      let data = await removeRole(id);
      if (data?.success) {
        await this.getRoleList();
      }
    },
    handleRowClick(row) {
      this.$router.push({
        name: "role_item_view",
        params: { id: row.code },
      });
    },
    search() {
      this.filters.page = 1;
    },
  },
};
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <div class="flex justify-between gap-5">
        <div class="relative" style="width: 320px">
          <Search
            class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          />
          <Input
            v-model="filters.q"
            :placeholder="$t('Поиск по ролям')"
            class="pl-9"
            @input="search"
          />
        </div>
        <Button v-if="canCreate" variant="default" @click="goToCreate"
          >{{ $t("Создать роль") }} +
        </Button>
      </div>

      <div class="table-area">
        <div class="h-full overflow-auto">
          <Table :loading="loading" :scroll-reset-key="filters.page">
            <TableHeader>
              <TableRow>
                <TableHead class="w-[68px]"></TableHead>
                <TableHead>{{ $t("Роль") }}</TableHead>
                <TableHead>{{ $t("Дата создания") }}</TableHead>
                <TableHead>{{ $t("Статус") }}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableEmpty v-if="!loading && !list.length" :colspan="4">
                {{ $t("Нет данных") }}
              </TableEmpty>
              <TableRow
                v-for="row in list"
                :key="row.code"
                class="cursor-pointer"
                @click="handleRowClick(row)"
              >
                <TableCell>
                  <TableRowActions
                    :actions="actions"
                    align="end"
                    @action="
                      handleActions({
                        action: $event.action,
                        uuid: row.code,
                        rowData: row,
                      })
                    "
                  />
                </TableCell>
                <TableCell>{{ row.name }}</TableCell>
                <TableCell>{{ dateFormat(row.created_at) }}</TableCell>
                <TableCell>
                  {{
                    row.status === "ACTIVE" ? $t("Активный") : $t("Неактивный")
                  }}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <TableFooterWrapper
          :total="total"
          v-model:pageSize="filters.page_size"
          v-model:currentPage="filters.page"
          :pageSizes="[1, 5, 10, 20]"
          :page-size="filters.page_size"
        >
          <template #pagination>
            <Pagination
              v-if="total > filters.page_size"
              :total="total"
              :items-per-page="filters.page_size"
              :page="filters.page"
              :sibling-count="1"
              :disabled="loading"
              show-edges
              @update:page="handlePageChange"
            >
              <PaginationContent v-slot="{ items }">
                <PaginationPrevious />
                <template v-for="(item, index) in items" :key="index">
                  <PaginationItem
                    v-if="item.type === 'page'"
                    :value="item.value"
                    :is-active="item.value === filters.page"
                  />
                  <PaginationEllipsis v-else />
                </template>
                <PaginationNext />
              </PaginationContent>
            </Pagination>
          </template>
        </TableFooterWrapper>
      </div>
    </div>
    <router-view></router-view>
  </div>
</template>

<style scoped lang="scss">
.next {
  @apply gap-4 border border-border;
  display: flex;
  padding: 4px 4px 4px 8px;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 8px;
  background-color: hsl(var(--muted));
}
.arrow-right {
  @apply border border-border;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px !important;

  height: 24px;
  border-radius: 4px;
  background-color: hsl(var(--card));

  svg {
    width: 12px;
    height: 12px;
  }
}
</style>
